import { useState } from "react";

import { useApi } from "./useApi";
import { useSwal } from "./useSwal";
import { transformData } from "@/utils/transformData";
import { attendanceIdcMap, reversedAttendanceIdcTypesMap } from "@/utils/mappings/idcMap";

export function useAttendanceIdcs() {
	const { api } = useApi();
	const { Toast } = useSwal();

	const [isLoadingAttendanceIdcs, setIsLoadingAttendanceIdcs] = useState(true);
	const [attendanceIdcs, setAttendanceIdcs] = useState<MedicalSurgicalActIdc[]>([]);

	const formatAttendanceIdcs = (data: GenericObject[]) =>
		transformData<AttendanceIdc[]>(data, attendanceIdcMap).map(
			({ id, code, description, ...attendanceIdc }) => ({
				id,
				idc: { code, description },
				types: {
					diagnosticHypothesis: Boolean(attendanceIdc.diagnosticHypothesis),
					diagnosisConfirmed: Boolean(attendanceIdc.diagnosisConfirmed),
					mainIdc: Boolean(attendanceIdc.mainIdc),
					chronicDisease: Boolean(attendanceIdc.chronicDisease),
				},
			})
		);

	async function getAttendanceIdcs(appointmentId: number) {
		try {
			setIsLoadingAttendanceIdcs(true);

			const { data } = await api.get(`/attendance/cid/${appointmentId}`);
			const formattedData = formatAttendanceIdcs(data);

			setAttendanceIdcs(formattedData);
		} catch (error) {
			console.log(error);
			Toast.fire({
				title: "Erro ao buscar CID's do atendimento!",
				icon: "error",
			});
		} finally {
			setIsLoadingAttendanceIdcs(false);
		}
	}

	async function createAttendanceIdc(attendanceId: number, idcCode: string | number) {
		try {
			const { data } = await api.post("/attendance/cid", {
				atendimento_id: attendanceId,
				ciddescricao: idcCode,
			});

			const formattedData = formatAttendanceIdcs(data.cids);

			setAttendanceIdcs(formattedData);
		} catch (error) {
			console.log(error);
			Toast.fire({
				title: "Erro ao criar CID!",
				icon: "error",
			});
		}
	}

	async function changeAttendanceIdcType(
		type: keyof MedicalSurgicalActIdcType,
		value: boolean,
		id: number
	) {
		try {
			setAttendanceIdcs((previous) =>
				previous.map((idc) => {
					if (id === idc.id) {
						return {
							...idc,
							types: {
								...idc.types,
								[type]: value,
							},
						};
					}

					return idc;
				})
			);

			const name = reversedAttendanceIdcTypesMap[type];

			await api.post("/attendance/cid/types", {
				id,
				name,
				value,
			});
		} catch (error) {
			console.log(error);
			Toast.fire({
				title: "Erro ao alterar tipo do CID!",
				text: "Atualize a página e tente novamente.",
				icon: "error",
			});
		}
	}

	async function removeAttendanceIdc(id: number) {
		try {
			setAttendanceIdcs((previous) => previous.filter((idc) => idc.id !== id));

			await api.delete(`/attendance/cid/${id}`);
		} catch (error) {
			console.log(error);
			Toast.fire({
				title: "Erro ao remover CID!",
				text: "Atualize a página e tente novamente.",
				icon: "error",
			});
		}
	}

	return {
		isLoadingAttendanceIdcs,
		attendanceIdcs,
		actions: {
			getAllBy: getAttendanceIdcs,
			create: createAttendanceIdc,
			changeType: changeAttendanceIdcType,
			remove: removeAttendanceIdc,
		},
	};
}
